/**
 * featureFlagsSync — bridge server-persisted lab_flags to client-side
 * featureFlags registry.
 *
 * v150 Phase 3 — the labFlags hook (queryKey ["lab-flags"]) GET-s
 * /athlete/profile/lab/flags. Whenever that data arrives, we call
 * setRemoteFlags() to mirror the overlapping subset into the client
 * featureFlags registry, so `useFeatureFlag('experimental_charts')`
 * sees the server-side opt-in.
 *
 * Only flags listed in `MIRROR` are bridged — server и client registries
 * are separate by design (server = athlete-facing experiments, client =
 * developer kill switches). Adding a new bridge = one entry in MIRROR.
 */
import { useEffect } from "react";
import { useLabFlags } from "../labFlags";
import { setRemoteFlags, type FlagName } from "./featureFlags";

/** Server lab-flag key → client featureFlags key. */
const MIRROR: Record<string, FlagName> = {
  experimental_charts: "experimental_charts",
};

export function useRemoteFlagsSync(): void {
  const { data } = useLabFlags();

  useEffect(() => {
    if (!data?.flags) return;
    const next: Partial<Record<FlagName, boolean>> = {};
    for (const [serverKey, clientKey] of Object.entries(MIRROR)) {
      const v = (data.flags as Record<string, boolean | undefined>)[serverKey];
      if (typeof v === "boolean") next[clientKey] = v;
    }
    if (Object.keys(next).length > 0) setRemoteFlags(next);
  }, [data]);
}
